import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'

export interface BreadcrumbItem {
  label: string
  href?: string
}

interface BreadcrumbContextValue {
  override: BreadcrumbItem[] | null
  setOverride: (items: BreadcrumbItem[] | null) => void
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  override: null,
  setOverride: () => {},
})

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [override, setOverrideRaw] = useState<BreadcrumbItem[] | null>(null)
  // rerender-functional-setstate: stable setter via useCallback
  const setOverride = useCallback((items: BreadcrumbItem[] | null) => {
    setOverrideRaw(items)
  }, [])

  return (
    <BreadcrumbContext.Provider value={{ override, setOverride }}>
      {children}
    </BreadcrumbContext.Provider>
  )
}

export function useBreadcrumbOverride() {
  return useContext(BreadcrumbContext)
}

/**
 * Set a breadcrumb override for the lifetime of the calling component.
 *
 * Pass the items to display (or `null`/`undefined` while data is still
 * loading — the override is left untouched in that case). Clears the
 * override on unmount so the next page falls back to URL-derived items.
 * Equivalent to the prior hand-rolled `useEffect(() => { setOverride(...);
 * return () => setOverride(null) }, [...])` block.
 *
 * Callers pass a fresh array literal each render, so the effect keys on a
 * serialized form of the items (not the array reference) — otherwise the
 * dep would change every render and `setOverride` would loop. Matches the
 * prior hand-rolled blocks, which depended on stable context objects.
 */
export function useBreadcrumb(items: BreadcrumbItem[] | null | undefined) {
  const { setOverride } = useBreadcrumbOverride()
  const key = JSON.stringify(items)
  useEffect(() => {
    if (!items) return
    setOverride(items)
    return () => setOverride(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setOverride])
}

